import React from "react";

/**
 * Safely parses and highlights search matches from Algolia/search _highlightResult
 * and/or an active text search query without using dangerouslySetInnerHTML.
 *
 * @param {Object} props
 * @param {Object} [props.hit] - Product or item object containing _highlightResult
 * @param {string} [props.attribute] - The property name (e.g., 'description', 'name', 'item_mfg_part')
 * @param {string} [props.fallback] - Fallback text if attribute is missing
 * @param {string} [props.text] - Direct text to highlight if not using hit/attribute
 * @param {string} [props.searchQuery] - Active typing query string for client-side fallback
 * @param {string} [props.className] - Optional custom CSS class for <mark>
 * @param {Object} [props.style] - Optional custom inline style for <mark>
 */
const DEFAULT_MARK_STYLE = {
  backgroundColor: "#fef08a",
  color: "#713f12",
  padding: "1px 3px",
  borderRadius: "3px",
  fontWeight: "600",
};

export const HighlightText = ({
  hit,
  attribute,
  fallback = "",
  text: directText,
  searchQuery = "",
  className = "search-highlight",
  style = DEFAULT_MARK_STYLE,
}) => {
  // 1. Resolve raw text and highlight data
  const highlightData = attribute && hit?._highlightResult ? hit._highlightResult[attribute] : null;
  let rawHighlightedStr = highlightData?.value;

  // Also check fallback attribute (e.g. if attribute is 'description', 'name' might have the highlight)
  if (!rawHighlightedStr && attribute === "description" && hit?._highlightResult?.name?.value) {
    rawHighlightedStr = hit._highlightResult.name.value;
  }

  // If attribute is 'item_mfg_part', check if 'sku' has the highlight and strip prefix if needed
  if (!rawHighlightedStr && attribute === "item_mfg_part" && hit?._highlightResult?.sku?.value) {
    const skuVal = hit._highlightResult.sku.value;
    if (skuVal.includes("-")) {
      rawHighlightedStr = skuVal.split("-").slice(1).join("-");
    } else {
      rawHighlightedStr = skuVal;
    }
  }

  const baseText = directText !== undefined
    ? String(directText ?? "")
    : (fallback || (attribute && hit?.[attribute]) || "");

  // 2. Case A: Search engine returned _highlightResult with <mark> tags
  if (rawHighlightedStr && typeof rawHighlightedStr === "string" && rawHighlightedStr.includes("<mark>")) {
    const parts = rawHighlightedStr.split(/(<mark>[\s\S]*?<\/mark>)/gi);
    return (
      <>
        {parts.map((part, index) => {
          const match = part.match(/^<mark>([\s\S]*?)<\/mark>$/i);
          if (match) {
            return (
              <mark key={index} className={className} style={style}>
                {match[1]}
              </mark>
            );
          }
          return <React.Fragment key={index}>{part}</React.Fragment>;
        })}
      </>
    );
  }

  // 3. Case B: Client-side fallback if searchQuery exists but _highlightResult didn't have tags
  const trimmedQuery = typeof searchQuery === "string" ? searchQuery.trim() : "";
  if (trimmedQuery && baseText && typeof baseText === "string") {
    const escapedQuery = trimmedQuery.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`(${escapedQuery})`, "gi");
    const parts = baseText.split(regex);

    if (parts.length > 1) {
      return (
        <>
          {parts.map((part, index) => {
            if (part.toLowerCase() === trimmedQuery.toLowerCase()) {
              return (
                <mark key={index} className={className} style={style}>
                  {part}
                </mark>
              );
            }
            return <React.Fragment key={index}>{part}</React.Fragment>;
          })}
        </>
      );
    }
  }

  // 4. Default: No matches -> return plain base text
  return <>{baseText}</>;
};

export default HighlightText;
